import React, { useEffect, useState } from 'react';
import { IFAQPageContent, IFAQItem, ScreenContentUtils } from '../MobileAppTypes';
import { IScreenEditorProps } from './SharedTypes';
import Button from '../../_Core/Button';

interface IFAQPageEditorProps extends IScreenEditorProps {
  content: IFAQPageContent;
}

const FAQPageEditor: React.FC<IFAQPageEditorProps> = ({
  content,
  errors,
  onContentChange,
  onFilesChange,
  onValidationChange
}) => {
  const [localContent, setLocalContent] = useState<IFAQPageContent>(() => {
    // Initialize with proper FAQ object parsing
    const initialContent = { ...content };
    try {
      const allowSearchValue = content['allowSearch'] as any;
      initialContent.allowSearch = allowSearchValue === 'true' || allowSearchValue === true;
      initialContent.faqObj = ScreenContentUtils.fromBase64ToObj(initialContent.faqs) || [];
    } catch {
      initialContent.faqObj = [];
    }
    return initialContent;
  });

  useEffect(() => {
    const updatedContent = { ...content };
    try {
      const allowSearchValue = content['allowSearch'] as any;
      updatedContent.allowSearch = allowSearchValue === 'true' || allowSearchValue === true;
      updatedContent.faqObj = ScreenContentUtils.fromBase64ToObj(updatedContent.faqs) || [];
    } catch {
      updatedContent.faqObj = [];
    }
    setLocalContent(updatedContent);
  }, [content]);

  const updateContent = (updates: Partial<IFAQPageContent>) => {
    const newContent = { ...localContent, ...updates };
    setLocalContent(newContent);
    onContentChange(newContent);
    
    // Validate on change
    const validationErrors = validate(newContent);
    onValidationChange(validationErrors);
  };

  const validate = (contentToValidate: IFAQPageContent = localContent): string[] => {
    const errors: string[] = [];
    if (!contentToValidate.title) errors.push('Page title is required');
    if (contentToValidate.faqObj.length === 0) {
      errors.push('At least one FAQ item is required');
    } else {
      contentToValidate.faqObj.forEach((faq, index) => {
        if (!faq.question.trim()) errors.push(`FAQ ${index + 1}: Question is required`);
        if (!faq.answer.trim()) errors.push(`FAQ ${index + 1}: Answer is required`);
      });
    }
    return errors;
  };

  const addFAQ = () => {
    const newFAQ: IFAQItem = {
      id: `faq_${Date.now()}`,
      question: '',
      answer: '',
      order: localContent.faqObj.length,
    };
    const faqs = [...localContent.faqObj, newFAQ];
    updateContent({
      faqLength: faqs.length,
      faqObj: faqs,
      faqs: ScreenContentUtils.objToBase64JsonString(faqs)
    });
  };

  const updateFAQ = (index: number, updates: Partial<IFAQItem>) => {
    const updatedFAQs = [...localContent.faqObj];
    updatedFAQs[index] = { ...updatedFAQs[index], ...updates };
    const faqs = updatedFAQs;
    updateContent({ 
      faqLength: faqs.length, 
      faqObj: updatedFAQs, 
      faqs: ScreenContentUtils.objToBase64JsonString(faqs) 
    });
  };

  const removeFAQ = (index: number) => {
    const updatedFAQs = localContent.faqObj.filter((_, i) => i !== index);
    const faqs = updatedFAQs;
    updateContent({ 
      faqLength: faqs.length, 
      faqObj: updatedFAQs, 
      faqs: ScreenContentUtils.objToBase64JsonString(faqs) 
    });
  };

  return (
    <div className="form-section">
      <h4>FAQ Page Content</h4>
      <div className="form-horizontal">
        <div className="form-group">
          <label className="col-sm-3 control-label">Page Title*</label>
          <div className="col-sm-9">
            <input 
              type="text" 
              className="form-control" 
              value={localContent.title} 
              onChange={(e) => updateContent({ title: e.target.value })} 
              placeholder="FAQ page title" 
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Description</label>
          <div className="col-sm-9">
            <textarea
              className="form-control"
              rows={3}
              value={localContent.description || ''}
              onChange={(e) => updateContent({ description: e.target.value })}
              placeholder="Optional description for the FAQ page"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">Allow Search</label>
          <div className="col-sm-9">
            <input 
              type="checkbox" 
              checked={localContent.allowSearch} 
              onChange={(e) => updateContent({ allowSearch: e.target.checked })} 
            />
            <span className="ml-2">Allow users to search through FAQs</span>
          </div>
        </div>

        <div className="form-group">
          <label className="col-sm-3 control-label">FAQs</label>
          <div className="col-sm-9">
            <Button type="new" text="Add FAQ" onClick={addFAQ} extraSmall />

            {localContent.faqObj.map((faq, index) => (
              <div key={faq.id} className="faq-item" style={{ border: '1px solid #ddd', padding: '10px', margin: '10px 0', borderRadius: '4px' }}>
                
                <div className='row'>
                  
                <div className="form-group col-sm-8">
                  <label>Question:</label>
                  <input 
                    type="text" 
                    className="form-control" 
                    value={faq.question} 
                    onChange={(e) => updateFAQ(index, { question: e.target.value })} 
                    placeholder="Enter the question" 
                  />
                </div>
                <div className='form-group col-sm-4'>
                  <label>Order:</label>
                  <input 
                    type="number" 
                    className="form-control" 
                    value={faq.order} 
                    onChange={(e) => updateFAQ(index, { order: parseInt(e.target.value) })} 
                    placeholder="Enter the order" 
                  />  
                </div>
                </div>

                <div className="form-group">
                  <label>Answer:</label>
                  <textarea 
                    className="form-control" 
                    rows={3} 
                    value={faq.answer} 
                    onChange={(e) => updateFAQ(index, { answer: e.target.value })} 
                    placeholder="Enter the answer" 
                  />
                </div>

                <div className="text-right">
                  <Button type="remove" text="Remove" onClick={() => removeFAQ(index)} extraSmall />
                </div>
              </div>
            ))}
            {!localContent.faqObj.length && <p>No FAQs added yet.</p>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FAQPageEditor;